//
// 03/05/2019 note:
// When changing any site settings, be sure to follow the process in the "Settings Support Work In-take Process" wiki page:
//    https://mytableau.tableaucorp.com/x/coDWD
//

import * as _ from 'underscore';

import { ActivitySpinnerProps, ActivitySpinnerSize } from '@tableau/widgets';
import { RemoteAgentPresenceIcon } from '../../app/content/RemoteAgentPresenceIcon';
import { Messages } from '../../codegen/Localize';
import { UserSettingsActions } from '../../ts/actions/UserSettingsActions';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ByteSizeConversionFactor } from '../../ts/filters/SizeFilter';
import { Module } from '../../ts/Module';
import { ExtractEncryptionMode, Sites } from '../../ts/resources/Sites';
import { IGetExtractEncryptionStatsResult } from '../../ts/ServerApi/requests/GetExtractEncryptionStatsRequest';
import { IError } from '../../ts/ServerApi/types/IError';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ExtractEncryptionService } from '../../ts/services/ExtractEncryptionService';
import { HelpService } from '../../ts/services/HelpService';
import { ServerService } from '../../ts/services/ServerService';
import { ISiteAuthenticationSettings, SiteAuthenticationSettingsService } from '../../ts/services/SiteAuthenticationSettingsService';
import { ISettingsCoordinatorCtrlScope, SettingsFormService } from '../directives/tbSettingsCoordinator';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '../interfaces/ISiteSettingsForSiteAdmin';

export interface IEncryptionStats {
  total: number;
  encrypted: number;
  unencrypted: number;
  encrypting: number;
  decrypting: number;
  inProgress: boolean;
}

export interface ISiteSettingsForSiteAdminFormScope extends ISettingsCoordinatorCtrlScope {
  siteSettingsForSiteAdminForm: ng.IFormController;
  remoteAgentPresenceIconProperties?: _.Dictionary<RemoteAgentPresenceIcon.Props>;
  siteRemoteAgentSettingsLearnMoreHref?: string;
  isSiteInviteNotificationUnavailable: boolean;
  extractEncryptionStats: IEncryptionStats;
  extractEncryptionStatusDescription: string;
  showStatus: boolean;
  extractEncryptionWarning: string;
  activitySpinnerProps: ActivitySpinnerProps;
  extractEncryptionStatusError: boolean;
  failureNotificationTitle: string;
  failureNotificationDetail: string;
  failureNotificationCheckboxLabel: string;
  showEARSetting: boolean;
  showSiteStartPageSetting: boolean;
  serverStartPage: string;  // Only shown to server admins
  siteStartPage: string;
  resetDefaultSiteStartPage: () => void;
  sandboxSiteStorageLimitRadioButtonLabel: string;
  sandboxUserStorageLimitRadioButtonLabel: string;
  alternativeFederationEngineUserInterface: boolean;
  afeUiSettingEnabled: boolean;
  derivedPermissionsLearnMoreHelpLink: string;
  metadataSettingEnabled: boolean;
}

export class SiteSettingsForSiteAdminFormCtrl {

  // master is being compared against this.scope.siteSettings which does not match the declared type at runtime
  private master: any;

  public static $inject = [
    '$scope',
    '$translate',
    '$state',
    'SettingsFormService',
    'Sites',
    'ServerService',
    'SiteAuthenticationSettingsService',
    'HelpService',
    '$timeout',
    'ExtractEncryptionService',
    'UserSettingsActions',
  ];

  constructor(
    private scope: ISiteSettingsForSiteAdminFormScope,
            $translate: ng.translate.ITranslateService,
    private $state: ng.ui.IStateService,
            formService: SettingsFormService,
    private sitesResource: Sites,
    private server: ServerService,
    private siteAuthenticationSettingsService: SiteAuthenticationSettingsService,
    private help: HelpService,
            $timeout: ng.ITimeoutService,
    private extractEncryptionService: ExtractEncryptionService,
    private userSettingsActions: UserSettingsActions,
  ) {
    this.scope.isSiteInviteNotificationUnavailable = this.isSiteInviteNotificationUnavailable(this.scope.siteAuthenticationSettings);

    // Site Start Page
    this.scope.showSiteStartPageSetting = FeatureFlags.siteDefaultStartPage && (this.server.isSessionMultiSite() || this.server.isSiteAdminSettingsEnabled());
    this.scope.siteStartPage = this.scope.siteSettings.siteAdminSettings ? (<ISiteAdminSettings>this.scope.siteSettings.siteAdminSettings).siteStartPageUri : '';
    this.scope.serverStartPage = this.getServerStartPage();
    this.scope.resetDefaultSiteStartPage = () => this.userSettingsActions.confirmResetSiteDefaultStartPage(() => {
      this.scope.siteStartPage = '';
    });

    this.scope.sharedForm = formService;
    this.scope.localForm = {
        formController: this.scope.siteSettingsForSiteAdminForm,
        save: () => this.getSiteSettingsForSiteAdminUpdatePromise(),
        cancel: () => this.revertChanges(),
        isBusy: () => this.scope.localForm.busy,
        isFormUnchanged: () => angular.equals(this.master, this.scope.siteSettings),
        resetForm: () => this.revertChanges(),
        resetSettings: null,
      };

    this.initializeUnsetValues();
    this.master = angular.copy(this.scope.siteSettings);

    // Extract Encryption properties.
    this.scope.showEARSetting = server.isExtractEncryptionEnabledOmniSwitch();
    if (this.scope.showEARSetting) {
      // This value controls whether the encryption status shows up or not in the view and controls whether the API to get status is called or not in the controller.
      this.scope.showStatus = this.scope.showEncryptionStatus;
      this.scope.extractEncryptionStatusError = false;
      this.scope.activitySpinnerProps = {
        testId: 'extract-encryption-spinner',
        spinnerSize: ActivitySpinnerSize.Small
      };
      this.getEncryptionStats();
      this.scope.$watch('siteSettings.siteAdminSettings.extractEncryptionMode', () => {
        this.shouldShowExtractEncryptionWarning();
      });
    }

    this.scope.alternativeFederationEngineUserInterface = FeatureFlags.alternativeFederationEngineUserInterface;
    this.scope.afeUiSettingEnabled = this.server.isAfeUiSettingEnabled;
    this.scope.derivedPermissionsLearnMoreHelpLink = help.getServerHelpUrlByCategory('dm-derived');
    this.scope.metadataSettingEnabled = this.server.catalogingOnSiteEnabled();
    this.failureNotificationContent();

    // TODO: Placeholder until Story 893142 is figured out.
    const siteStorageQuotaBytes = 5000000000;
    this.populateSandboxLabels(siteStorageQuotaBytes);
  }

  private initializeUnsetValues() {
    if (this.scope.siteSettings && this.scope.siteSettings.siteAdminSettings) {
      let siteAdminSettings = (<ISiteAdminSettings> this.scope.siteSettings.siteAdminSettings);
      if (siteAdminSettings && siteAdminSettings.siteSandboxSettings && !siteAdminSettings.siteSandboxSettings.sandboxStorageQuotaSet) {
        siteAdminSettings.siteSandboxSettings.sandboxStorageQuota = null;
      }
    }
  }

  public failureNotificationContent() {
    const showFailureNotifForEAR = this.scope.showEARSetting;
    if(showFailureNotifForEAR && this.server.flowsOnSiteEnabled()) {
      this.scope.failureNotificationTitle = Messages.refreshOrFlowOrEncryptionRunFailureEmailNotificationSettingSection();
      this.scope.failureNotificationDetail = Messages.refreshOrFlowOrEncryptionRunFailureEmailNotificationSiteSettingDetail();
      this.scope.failureNotificationCheckboxLabel = Messages.refreshOrFlowOrEncryptionRunFailureEmailNotificationSiteSettingEnable();
    } else if (showFailureNotifForEAR && !this.server.flowsOnSiteEnabled()) {
      this.scope.failureNotificationTitle = Messages.refreshOrEncryptionRunFailureEmailNotificationSettingSection();
      this.scope.failureNotificationDetail = Messages.refreshOrEncryptionRunFailureEmailNotificationSiteSettingDetail();
      this.scope.failureNotificationCheckboxLabel = Messages.refreshOrEncryptionRunFailureEmailNotificationSiteSettingEnable();
    } else if (!showFailureNotifForEAR && this.server.flowsOnSiteEnabled()) {
      this.scope.failureNotificationTitle = Messages.refreshOrFlowRunFailureEmailNotificationSettingSection();
      this.scope.failureNotificationDetail = Messages.refreshOrFlowRunFailureEmailNotificationSiteSettingDetail();
      this.scope.failureNotificationCheckboxLabel = Messages.refreshOrFlowRunFailureEmailNotificationSiteSettingEnable();
    } else {
      this.scope.failureNotificationTitle = Messages.refreshFailureEmailNotificationSettingSection();
      this.scope.failureNotificationDetail = Messages.refreshFailureEmailNotificationSiteSettingDetail();
      this.scope.failureNotificationCheckboxLabel = Messages.refreshFailureEmailNotificationSiteSettingEnable();
    }
  }

  private bytesToGBConverter(bytes: number) : string {
    return Math.floor(bytes / ByteSizeConversionFactor.ONE_GB).toFixed(0);
  }

  public populateSandboxLabels(siteStorageQuotaBytes: number) {
    const siteStorageQuotaStr = this.bytesToGBConverter(siteStorageQuotaBytes);
    this.scope.sandboxSiteStorageLimitRadioButtonLabel = Messages.sandboxSiteSettingStorageLimitSiteQuota({SITE_LIMIT: siteStorageQuotaStr});
    this.scope.sandboxUserStorageLimitRadioButtonLabel = Messages.sandboxSiteSettingStorageLimitCustomQuota({SITE_LIMIT: siteStorageQuotaStr});
  }

  public getExtractEncryptionStatusDescription(data: IGetExtractEncryptionStatsResult): string {
    if(data.counts.encrypting > 0 && data.counts.decrypting > 0) {
      return Messages.ear_Status_Encryption_Decryption_In_Progress();
    } else if(data.counts.encrypting > 0) {
      return Messages.ear_Status_Encryption_In_Progress();
    } else if(data.counts.decrypting > 0) {
      return Messages.ear_Status_Decryption_In_Progress();
    } else {
      return Messages.ear_Status_Encryption_Not_In_Progress();
    }
  }

  public shouldShowExtractEncryptionWarning() {
    const siteAdminSettings = this.scope.siteSettings.siteAdminSettings as ISiteAdminSettings;
    if (!this.scope.showStatus || this.master.siteAdminSettings.extractEncryptionMode === siteAdminSettings.extractEncryptionMode) {
      // no warning if status is hidden or option is not changed.
      this.scope.extractEncryptionWarning = '';
    } else if (siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.DISABLED) {
      this.scope.extractEncryptionWarning = Messages.ear_Disable_Warning();
    } else if (siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.ENFORCED) {
      this.scope.extractEncryptionWarning = Messages.ear_Enforce_Warning();
    } else if (siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.ENABLED &&
              this.master.siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.DISABLED) {
      this.scope.extractEncryptionWarning = Messages.ear_Disable_To_Enable_Warning();
    } else if (siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.ENABLED &&
              this.master.siteAdminSettings.extractEncryptionMode === ExtractEncryptionMode.ENFORCED) {
      this.scope.extractEncryptionWarning = Messages.ear_Enforce_To_Enable_Warning();
    } else {
      this.scope.extractEncryptionWarning = '';
    }
  }

  public getEncryptionStats() {
    // Show encryption status
    if (this.scope.showStatus) {
      this.pollEncryptionStats();
      // When this compnent is destroyed we want the polling to stop.
      this.scope.$on('$destroy', () => {
        this.extractEncryptionService.cancelExtractEncryptionStatsPolling();
      });
    }
  }

  // start polling for extract encryption stats
  // This method should never be called directly and only through getEncryptionStats()
  private pollEncryptionStats() {
    const site: ISiteSettingsForSiteAdmin = angular.copy(this.scope.siteSettings);
    const siteId = (site as any).id || site.siteId;
    this.extractEncryptionService.pollExtractEncryptionStats(siteId).then(
      (result: IGetExtractEncryptionStatsResult) => {
        //completed
        this.UpdateEncryptionStats(result);
      },
      () => {
        //error
        this.scope.extractEncryptionStatusError = true;
      },
      (result: IGetExtractEncryptionStatsResult) => {
        //notify
        this.UpdateEncryptionStats(result);
      }
    );
  }

  private isExtractEncryptionStatsValid(data: IGetExtractEncryptionStatsResult): boolean {
    if(data && data.counts) {
      return true;
    }
    // This failed due to invalid data we want to cancel the polling
    this.extractEncryptionService.cancelExtractEncryptionStatsPolling();
    this.scope.extractEncryptionStatusError = true;
    return false;
  }

  //Update scope variables with the extract encryption stats
  public UpdateEncryptionStats(data: IGetExtractEncryptionStatsResult) {
    // Check validity of the api response
    if(this.isExtractEncryptionStatsValid(data)) {
      this.scope.extractEncryptionStats = {
        total: data.counts.decrypting + data.counts.encrypting + data.counts.encrypted + data.counts.unencrypted,
        unencrypted: data.counts.encrypting + data.counts.unencrypted,
        encrypted: data.counts.decrypting + data.counts.encrypted,
        encrypting: data.counts.encrypting,
        decrypting: data.counts.decrypting,
        inProgress: data.counts.encrypting > 0 || data.counts.decrypting > 0
      };
      this.scope.extractEncryptionStatusDescription = this.getExtractEncryptionStatusDescription(data);
    }
  }

  private revertChanges(): void {
    this.scope.siteSettings = angular.copy(this.master);
    // clear the warning when the changes are reverted
    // TODO: Use Angular 1.3 $validators/$validate and pull validation out of $parsers/$formatters
    _.each(this.scope.localForm.formController.$error, (error: ng.INgModelController[], key: string) => {
      if (error) {
        error[0].$setValidity(key, true);
      }
    });
  }

  private isSiteInviteNotificationUnavailable(settings: ISiteAuthenticationSettings): boolean {
    // If the site isn't using SSO auth, the site invite feature is unavailable
    return !this.siteAuthenticationSettingsService.isAuthenticationSSO(settings);
  }

  // used by EditSiteAction and in the Site Settings page
  private getSiteSettingsForSiteAdminUpdatePromise(): ng.IPromise<any> {
    const site: ISiteSettingsForSiteAdmin = angular.copy(this.scope.siteSettings);
    let siteAdminSettings: ISiteAdminSettings = site.siteAdminSettings as ISiteAdminSettings; //typechecking union type

    const siteId = (site as any).id || site.siteId; //todo - clean up entry paths to here so it is always the same
    this.scope.localForm.busy = true;
    let errors: IError[] = [];
    return this.sitesResource.updateSiteSettingsForSiteAdmin(siteId,
        siteAdminSettings).then((result: IResult) => {
      if (result.errors && result.errors.length) {
        errors = errors.concat(result.errors);
        this.scope.sharedForm.state.updateFailure(errors, null);
      } else {
        this.updateSuccess(site);
      }
    })
    .finally(() => {
      this.scope.localForm.busy = false;
      // This is to check the encryption status after the setting is saved.
      this.getEncryptionStats();
      // As the setting is saved we need to clear the warning.
      this.scope.extractEncryptionWarning = '';
    });
  }

  // Success/Error Handlers
  private updateSuccess(site: any): void {
    // Only need to update current view and state if we're in server or site settings
    if (!this.$state.includes('**.settings.**')) {
      return;
    }

    // If we changed extractEncryptionMode, we need to let the ServerService know
    if (site.siteAdminSettings &&
          this.master.siteAdminSettings &&
          (site.siteAdminSettings.extractEncryptionMode !== this.master.siteAdminSettings.extractEncryptionMode)) {
      this.scope.server.refreshSessionInfo();
    }

    this.master = angular.copy(site);
  }

  private getServerStartPage(): string {
    if (!this.scope.serverSettings)
      return '/';

    return this.scope.serverSettings.defaultStartPage || '/';
  }
}

Module.instance().controller('SiteSettingsForSiteAdminFormCtrl', SiteSettingsForSiteAdminFormCtrl);
